import "./App.css";
import "./imperio.css";
import "./binance-skin.css";
import { lazy as reactLazy, Suspense, Component } from "react";

// After a new deploy the old chunk files are gone: reload once to fetch the new build instead of a white screen.
const lazy = (factory) => reactLazy(() => factory().then((m) => { sessionStorage.removeItem("imp_chunk_reload"); return m; }).catch((err) => {
  if (!sessionStorage.getItem("imp_chunk_reload")) { sessionStorage.setItem("imp_chunk_reload", "1"); window.location.reload(); return new Promise(() => {}); }
  throw err;
}));

// Reset the error boundary on navigation so one crashed page doesn't blank every other route.
const RouteBoundary = ({ children }) => <AppErrorBoundary key={useLocation().pathname}>{children}</AppErrorBoundary>;
// Home renders its own Azbit-style footer; public info pages get the Azbit footer too.
const MarketingFooter = () => {
  const p = useLocation().pathname;
  if (p === "/") return null;
  return isAzPage(p) ? <AzPageFooter /> : <><AzPageFooter /><SiteFooter /></>;
};

class AppErrorBoundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, fontFamily: "inherit" }} data-testid="app-error-boundary">
        <h2 style={{ margin: 0 }}>Something went wrong loading this page.</h2>
        <p style={{ margin: 0, opacity: 0.7 }}>Please reload. If this keeps happening, contact support.</p>
        <button className="button blue" onClick={() => { sessionStorage.removeItem("imp_chunk_reload"); window.location.reload(); }} data-testid="app-error-reload">Reload</button>
      </div>
    );
  }
}
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import AzHome from "./components/azhome/AzHome";
import { AzPageFooter, isAzPage } from "./components/azhome/AzPageChrome";
import { CookieBar } from "./components/azhome/CookieBar";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
// Code-split: everything beyond the landing/auth pages loads on demand, so first
// paint ships a fraction of the JS (admin console, chart engine, KYC, etc.).
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ListYourToken = lazy(() => import("./pages/ListYourToken"));
const Trade = lazy(() => import("./pages/Trade"));
const Futures = lazy(() => import("./pages/Futures"));
const Wallet = lazy(() => import("./pages/Wallet"));
const WalletHistory = lazy(() => import("./pages/WalletHistory"));
const AddressBookPage = lazy(() => import("./pages/AddressBook"));
const AddressNotMe = lazy(() => import("./pages/AddressNotMe"));
const ImrioToken = lazy(() => import("./pages/ImrioToken"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminConsole = lazy(() => import("./pages/AdminConsole"));
const Assets = lazy(() => import("./pages/Assets"));
const PnlAnalysis = lazy(() => import("./pages/PnlAnalysis"));
const Kyc = lazy(() => import("./pages/Kyc"));
const Orders = lazy(() => import("./pages/Orders"));
const Referral = lazy(() => import("./pages/Referral"));
const Earn = lazy(() => import("./pages/Earn"));
const Convert = lazy(() => import("./pages/Convert"));
const Rewards = lazy(() => import("./pages/Rewards"));
const SubAccounts = lazy(() => import("./pages/SubAccounts"));
const Security = lazy(() => import("./pages/Security"));
const EarnHistory = lazy(() => import("./pages/EarnHistory"));
const ConvertHistory = lazy(() => import("./pages/ConvertHistory"));
const SettingsPage = lazy(() => import("./pages/Settings"));
import SiteFooter from "./components/SiteFooter";
import AnnouncementBanner from "./components/AnnouncementBanner";
const AdminFooter = lazy(() => import("./pages/AdminFooter"));
const AdminPages = lazy(() => import("./pages/AdminPages"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const PublicPage = lazy(() => import("./pages/PublicPage"));
const Blogs = lazy(() => import("./pages/Blogs"));
const Contact = lazy(() => import("./pages/Contact"));
const InsuranceFund = lazy(() => import("./pages/InsuranceFund"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Support = lazy(() => import("./pages/Support"));
import MobileTabBar from "./components/MobileTabBar";

const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <div className="auth-submit" style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid rgba(59,130,246,.25)", borderTopColor: "#3b82f6", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

// The dedicated admin deployment (REACT_APP_ADMIN_MODE=true, the ops domain) exposes
// ONLY the console: every customer/marketing route bounces to /admin.
const ADMIN_MODE = process.env.REACT_APP_ADMIN_MODE === "true";
const Guarded = ({ children }) => (ADMIN_MODE ? <Navigate to="/admin" replace /> : children);

const Home = () => (
  <main id="top">
    <AzHome />
  </main>
);

function App() {
  return (
    <BrowserRouter>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <AuthProvider>
        <AnnouncementBanner />
        <RouteBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={ADMIN_MODE ? <Navigate to="/admin" replace /> : <Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/security/not-me" element={<AddressNotMe />} />
          <Route path="/imrio" element={<ImrioToken />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
          <Route path="/register" element={ADMIN_MODE ? <Navigate to="/login" replace /> : <Register />} />
          <Route path="/dashboard" element={<Guarded><Dashboard /></Guarded>} />
          <Route path="/trade" element={<Guarded><Trade /></Guarded>} />
          <Route path="/futures" element={<Guarded><Futures /></Guarded>} />
          <Route path="/insurance-fund" element={<Guarded><InsuranceFund /></Guarded>} />
          <Route path="/wallet" element={<Guarded><Wallet /></Guarded>} />
          <Route path="/wallet/history" element={<Guarded><WalletHistory /></Guarded>} />
          <Route path="/address-book" element={<Guarded><AddressBookPage /></Guarded>} />
          <Route path="/assets" element={<Guarded><Assets /></Guarded>} />
          <Route path="/pnl-analysis" element={<Guarded><PnlAnalysis /></Guarded>} />
          <Route path="/orders" element={<Guarded><Orders /></Guarded>} />
          <Route path="/orders/earn" element={<Guarded><EarnHistory /></Guarded>} />
          <Route path="/orders/convert" element={<Guarded><ConvertHistory /></Guarded>} />
          <Route path="/referral" element={<Guarded><Referral /></Guarded>} />
          <Route path="/earn" element={<Guarded><Earn /></Guarded>} />
          <Route path="/convert" element={<Guarded><Convert /></Guarded>} />
          <Route path="/rewards" element={<Guarded><Rewards /></Guarded>} />
          <Route path="/subaccounts" element={<Guarded><SubAccounts /></Guarded>} />
          <Route path="/security" element={<Guarded><Security /></Guarded>} />
          <Route path="/settings" element={<Guarded><SettingsPage /></Guarded>} />
          <Route path="/support" element={<Guarded><Support /></Guarded>} />
          <Route path="/kyc" element={<Guarded><Kyc /></Guarded>} />
          <Route path="/admin" element={<AdminConsole />} />
          <Route path="/admin/footer" element={<AdminFooter />} />
          <Route path="/admin/pages" element={<AdminPages />} />
          <Route path="/p/:slug" element={<PublicPage />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/list-your-token" element={<ListYourToken />} />
          <Route path="/blogs/:slug" element={<BlogPost />} />
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/admin/api-docs" element={<ApiDocs />} />
          <Route path="/developers" element={<ApiDocs />} />
          <Route path="/admin/legacy" element={<Admin />} />
        </Routes>
        </Suspense>
        </RouteBoundary>
        {!ADMIN_MODE && <MobileTabBar />}
        {!ADMIN_MODE && <MarketingFooter />}
        {!ADMIN_MODE && <CookieBar />}
        <Toaster position="top-right" richColors closeButton theme={localStorage.getItem("imperio_theme") !== "light" ? "dark" : "light"} />
      </AuthProvider>
      </GoogleOAuthProvider>
    </BrowserRouter>
  );
}

export default App;
